import { FEVER_C, FEVER_WATCH_C } from '@oncocoach/care';

/**
 * Seven days of temperature as an inline SVG line chart. Gaps are days without one.
 *
 * The 38.0 °C line is drawn across the whole chart and labelled, because the shape of the line
 * matters less than whether it has crossed that one number. The scale is fixed around body
 * temperature rather than fitted to the data, so a normal week looks flat instead of dramatic.
 */
export function TempChart({ trend }: { trend: Array<{ label: string; celsius: number | null }> }) {
  const w = 640;
  const h = 260;
  const pad = { l: 42, r: 14, t: 16, b: 30 };
  const values = trend.flatMap((t) => (t.celsius !== null ? [t.celsius] : []));
  const lo = Math.min(35.5, ...values) - 0.3;
  const hi = Math.max(38.6, ...values) + 0.3;
  const x = (i: number) => pad.l + (i * (w - pad.l - pad.r)) / Math.max(1, trend.length - 1);
  const y = (v: number) => pad.t + ((hi - v) * (h - pad.t - pad.b)) / (hi - lo);
  const ticks = [36, 37, 38].filter((v) => v > lo && v < hi);

  let path = '';
  trend.forEach((t, i) => {
    if (t.celsius === null) return;
    const point = `${x(i).toFixed(1)},${y(t.celsius).toFixed(1)}`;
    path += !path || trend[i - 1]?.celsius === null || trend[i - 1] === undefined ? ` M${point}` : ` L${point}`;
  });

  return (
    <svg className="temp-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label="Seven-day temperature trend">
      {ticks.map((v) => (
        <g key={v}>
          <line className="grid-line" x1={pad.l} x2={w - pad.r} y1={y(v)} y2={y(v)} />
          <text className="axis" x={pad.l - 8} y={y(v) + 4} textAnchor="end">
            {v}°
          </text>
        </g>
      ))}
      <line className="line-watch" x1={pad.l} x2={w - pad.r} y1={y(FEVER_WATCH_C)} y2={y(FEVER_WATCH_C)} />
      <line className="line-fever" x1={pad.l} x2={w - pad.r} y1={y(FEVER_C)} y2={y(FEVER_C)} />
      <text className="axis axis-fever" x={w - pad.r} y={y(FEVER_C) - 6} textAnchor="end">
        {FEVER_C.toFixed(1)}° — ring your team
      </text>
      <path d={path.trim()} className="line-temp" />
      {trend.map((t, i) =>
        t.celsius !== null ? <circle key={`p${i}`} cx={x(i)} cy={y(t.celsius)} r={3.5} className={t.celsius >= FEVER_C ? 'dot-fever' : 'dot-temp'} /> : null,
      )}
      {trend.map((t, i) => (
        <text key={`l${i}`} className="axis" x={x(i)} y={h - 8} textAnchor="middle">
          {t.label}
        </text>
      ))}
    </svg>
  );
}
